import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../axios';

export interface UserType {
  id: string;
  email: string;
  phoneNumber?: string | null;
  emailNotificationsEnabled?: boolean;
  role: 'ADMIN' | 'PARTNER' | 'CLOSER';
  status: 'ACTIVE' | 'BLOCKED';
  closerType?: 'TEAM_MEMBER' | 'EXTERNAL' | null;
  salesCompensationModel?: 'FIXED_1200' | 'FIXED_500' | null;
}

interface AuthState {
  isAuthenticated: boolean;
  user: UserType | null;
  initialized: boolean;
  loading: 'idle' | 'pending' | 'succeeded' | 'failed';
  error: string | null;
}

interface LoginPayload {
  email: string;
  password: string;
}

interface LoginApiResponse {
  id: string;
  email: string;
  role: 'ADMIN' | 'PARTNER' | 'CLOSER';
}

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  initialized: false,
  loading: 'idle',
  error: null,
};

const getErrorMessage = (payload: unknown, fallback: string): string => {
  if (typeof payload === 'string' && payload.trim()) {
    return payload;
  }
  if (payload && typeof payload === 'object' && 'message' in payload) {
    const message = (payload as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }
  return fallback;
};

export const login = createAsyncThunk<UserType, LoginPayload>(
  'auth/login',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      await axios.post<LoginApiResponse>('/api/auth/signin', {
        email,
        password,
      });
      const profileResponse = await axios.get<UserType>('/api/auth/me');
      return profileResponse.data;
    } catch (error: any) {
      if (error.response?.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue('Login failed');
    }
  }
);

export const logout = createAsyncThunk('auth/logout', async (_, { rejectWithValue }) => {
  try {
    await axios.post('/api/auth/logout');
    return null;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || 'Logout failed');
  }
});

export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get<UserType>('/api/auth/me');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Failed to get user');
    }
  }
);

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearAuth: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.initialized = true;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = 'pending';
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isAuthenticated = true;
        state.user = action.payload;
        state.initialized = true;
        state.loading = 'succeeded';
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = 'failed';
        state.error = getErrorMessage(action.payload, 'Login failed');
        state.isAuthenticated = false;
        state.user = null;
        state.initialized = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.initialized = true;
        state.error = null;
      })
      .addCase(getCurrentUser.pending, (state) => {
        state.loading = 'pending';
        state.error = null;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.isAuthenticated = true;
        state.user = action.payload;
        state.initialized = true;
        state.loading = 'succeeded';
      })
      .addCase(getCurrentUser.rejected, (state) => {
        state.loading = 'failed';
        state.error = null;
        state.isAuthenticated = false;
        state.user = null;
        state.initialized = true;
      });
  },
});

export const { clearAuth } = authSlice.actions;
export default authSlice.reducer;
